import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { createPasswordApi } from "../../api/createPasswordApi";
import { statusApi } from "../../api/statusApi";
import { onboardingApi } from "../../api/onboardingApi";
import { resumeApi } from "../../api/resumeApi";
import {
  isErrorWithStatus,
  isRequestError,
  RequestError,
} from "../../types/error.type";
import {
  AlertMessageType,
  StateStatus,
  WorkflowType,
} from "../../types/state.type";
import { noAlertErrors } from "../../utils/error.utils";
import {
  allFulfilledMatchers,
  allPendingMatchers,
  allRejectedMatchers,
} from "../genericState/genericState";

type UIState = {
  status: StateStatus;
  errorMessage?: string;
  errorCode?: string | number;
  errorData?: RequestError;
  alert: AlertMessageType;
  validatingFields: string[];
  currentWorkflow?: WorkflowType;
  lastHttpResponseStatus?: number;
};
const initialState: UIState = {
  status: "idle",
  errorMessage: "",
  validatingFields: [],
  alert: {
    severity: undefined,
  },
};

export const uiSlice = createSlice({
  name: "@Ui",
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
    setStatus: (state, value: PayloadAction<StateStatus>) => {
      state.status = value.payload;
    },
    setCurrentWorkflow: (
      state,
      action: PayloadAction<WorkflowType | undefined>,
    ) => {
      state.currentWorkflow = action.payload;
    },
    setErrorMessage(state, action: PayloadAction<string>) {
      state.errorMessage = action.payload;
    },
    setAlertMessage(state, action: PayloadAction<AlertMessageType>) {
      state.alert = action.payload;
    },
    removeAlertMessage(state) {
      state.alert = initialState.alert;
    },
    stopFieldValidation(s, a: PayloadAction<string>) {
      s.validatingFields = s.validatingFields.filter(
        field => field !== a.payload,
      );
    },
    stopAllFieldValidations(s) {
      s.validatingFields = [];
    },
    startFieldValidation(s, a: PayloadAction<string>) {
      if (!s.validatingFields.includes(a.payload)) {
        s.validatingFields = s.validatingFields.concat(a.payload);
      }
    },
    setErrorCode(state, action: PayloadAction<number | string | undefined>) {
      state.errorCode = action.payload;
      state.status = "error";
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        isAnyOf(...allRejectedMatchers),
        (state, action: PayloadAction<unknown>) => {
          const { payload } = action;
          let shouldSkipAlert = false;

          state.status = "error";
          if (isErrorWithStatus(payload)) {
            state.errorCode = payload.status;
            state.lastHttpResponseStatus = payload.status;
            shouldSkipAlert = noAlertErrors.includes(payload.code);
          }

          if (isRequestError(payload)) {
            state.errorData = payload;
            const { data, blocking } = payload;
            if (typeof data === "object" && !blocking && !shouldSkipAlert) {
              if (data && data.errorCode) {
                state.alert.severity = "error";
                state.alert.errorCode = data.errorCode;
                state.alert.message = data.errorMessage;
              } else {
                state.alert.severity = "error";
                state.alert.errorCode = "GENERIC";
                state.alert.message = "Errore generico";
              }
            }
          } else {
            // dot not show the alert message for validation errors
            if (isErrorWithStatus(payload) && !shouldSkipAlert) {
              state.alert.severity = "error";
              state.alert.errorCode = "GENERIC";
              state.alert.message = "Errore generico" + JSON.stringify(payload);
            } else {
              state.alert.severity = "error";
              state.alert.errorCode = "GENERIC";
              state.alert.message = "Errore generico";
            }
          }
        },
      )
      .addMatcher(isAnyOf(...allPendingMatchers), state => {
        state.lastHttpResponseStatus = undefined;
        state.status = "idle";
        state.errorCode = "";
        state.errorData = undefined;
        state.errorMessage = "";
        state.errorData = undefined;
        // reset the alert object when fetching new steps.
        state.alert = {
          severity: undefined,
          errorCode: "",
          message: "",
          title: "",
        };
      })
      .addMatcher(
        isAnyOf(onboardingApi.endpoints.start.matchPending),
        state => {
          state.currentWorkflow = "onboarding";
        },
      )
      .addMatcher(
        isAnyOf(resumeApi.endpoints.resumeStart.matchPending),
        state => {
          state.currentWorkflow = "resume";
        },
      )
      .addMatcher(
        isAnyOf(statusApi.endpoints.statusStart.matchPending),
        state => {
          state.currentWorkflow = "status";
        },
      )
      .addMatcher(
        isAnyOf(createPasswordApi.endpoints.startPasswordCreate.matchPending),
        state => {
          state.currentWorkflow = "createPassword";
        },
      )
      // track the last http response status for all RTK api calls.
      .addMatcher(
        isAnyOf(...allRejectedMatchers, ...allFulfilledMatchers),
        (state, payload) => {
          state.lastHttpResponseStatus = 200;
          const {
            meta: { baseQueryMeta },
          } = payload;
          if (baseQueryMeta) {
            const response = ((baseQueryMeta as Record<string, unknown>) || {})
              .response as Response;
            if (response) {
              state.lastHttpResponseStatus = response.status;
            }
          }
        },
      );
  },
});

export const uiActions = uiSlice.actions;
export default uiSlice;
